/**
 * lrb-signals.js — applies Mission Control signals on the storefront
 * Leaps & Rebounds
 *
 * A signal is a tag a visitor earns by doing something: reading a weight-limit
 * post earns plus_sized, arriving from a knee-pain ad earns knee_pain. The rules
 * live in Mission Control → Website → Signals (the signal_definitions table);
 * this file fetches them, checks the current page against them, and remembers
 * every match in localStorage so the tag follows the visitor to every later
 * page — that is the whole point: read the post once, see plus-size content on
 * the product page afterwards.
 *
 * It is loaded site-wide from layout/theme.liquid, separately from
 * lrb-personalization.js, because most pages that should award a signal (blog
 * posts, workout pages) have no personalization embed on them at all. Like
 * lrb-params.js it must survive the personalization layer being absent.
 *
 * Rules it applies, per definition (trigger_config):
 *   url_contains: "/blogs/x" or ["/blogs/x", "/blogs/y"]
 *                 matches when the path contains any entry (case-insensitive)
 *   param + value: matches when ?<param> contains <value>
 *   param + present: true (or value: "*"): matches when ?<param> has any value
 * A definition may carry both; either one matching awards the signal.
 * expires_after_ms on the definition bounds how long a match is remembered;
 * null means for good. time_based and manual signals are not applied here —
 * first_time_user / returning belong to lrb-personalization.js, manual and
 * webhook signals to the server.
 *
 * Published API, for lrb-personalization.js and for debugging:
 *   window.LRB_SIGNALS.ready   Promise<string[]> — resolves once this page has
 *                              been checked (never rejects; falls back to what
 *                              is already remembered if the rules cannot load)
 *   window.LRB_SIGNALS.tags()  string[] — every signal currently held
 *   window.LRB_SIGNALS.has(k)  boolean
 *   window.LRB_SIGNALS.clear() forget everything (QA)
 *
 * ?lrb_debug=1 logs every decision to the console.
 */

(() => {
  'use strict';

  if (window.LRB_SIGNALS) return; // loaded twice (theme.liquid + an embed section)

  const CONFIG = window.LRB_INTELLIGENCE_CONFIG || {};
  const BOT_URL = CONFIG.botUrl || 'https://gravity-claw-production-fb9e.up.railway.app';
  const DEFS_URL = `${BOT_URL}/admin/intelligence/signals/active`;

  const DEFS_KEY = 'lrb_signal_defs'; // { ts, defs }
  const STORE_KEY = 'lrb_signals'; // { <key>: { at, exp } }
  const DEFS_FRESH_MS = 30 * 60 * 1000; // refetch rules after 30 min
  const DEFS_MAX_MS = 7 * 24 * 60 * 60 * 1000; // never apply rules older than a week
  const FETCH_TIMEOUT = 2500;

  const qs = new URLSearchParams(window.location.search);
  const DEV = qs.get('lrb_debug') === '1';
  // Same preview rule as lrb-personalization.js: a QA session or a non-live
  // theme (layout/theme.liquid) is internal traffic, so the profile write
  // below carries preview: true and gravity-claw does not save it.
  const PREVIEW = DEV || (window.LRB_PREVIEW_THEME === true && qs.get('lrb_preview') !== '0');
  const log = (...args) => {
    if (DEV) console.log('[LRB signals]', ...args);
  };

  // ── Storage (every access can throw: private mode, blocked site data) ────────

  const readJSON = key => {
    try {
      const raw = localStorage.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  };
  const writeJSON = (key, value) => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {
      /* not persisted */
    }
  };

  const readStore = () => {
    const saved = readJSON(STORE_KEY);
    if (!saved || typeof saved !== 'object') return {};
    const now = Date.now();
    const live = {};
    for (const [k, s] of Object.entries(saved)) {
      if (s && (s.exp == null || s.exp > now)) live[k] = s;
    }
    return live;
  };

  let store = readStore();

  // ── Matching ─────────────────────────────────────────────────────────────────

  const normPath = p =>
    (
      String(p || '')
        .split('?')[0]
        .replace(/\/+$/, '') || '/'
    ).toLowerCase();
  const PATH = normPath(window.location.pathname);

  const matches = def => {
    const cfg = def.trigger_config || {};

    let urls = cfg.url_contains;
    if (typeof urls === 'string') urls = [urls];
    if (Array.isArray(urls)) {
      for (const entry of urls) {
        const u = String(entry || '')
          .trim()
          .toLowerCase()
          .replace(/\/+$/, '');
        if (u && PATH.includes(u)) return `url ${u}`;
      }
    }

    if (cfg.param && (cfg.present === true || cfg.value === '*')) {
      // Presence: for ad click ids (fbclid, gclid) whose values are random.
      if (qs.get(String(cfg.param))) return `${cfg.param} present`;
    } else if (cfg.param && cfg.value) {
      const have = (qs.get(String(cfg.param)) || '').toLowerCase();
      if (have && have.includes(String(cfg.value).toLowerCase())) return `${cfg.param}=${have}`;
    }
    return null;
  };

  /** Checks this page against the rules. Returns the keys newly earned on it. */
  const apply = defs => {
    const now = Date.now();
    const known = new Set();
    const earned = [];
    for (const def of defs) {
      if (!def?.key) continue;
      known.add(def.key);
      const why = matches(def);
      if (!why) continue;
      const ms = def.expires_after_ms;
      if (!store[def.key]) earned.push(def.key);
      // A repeat match renews an expiring signal; the first-seen time is kept.
      store[def.key] = {
        at: store[def.key] ? store[def.key].at : now,
        exp: ms ? now + Number(ms) : null,
      };
      log('match', def.key, '←', why);
    }
    // A signal switched off or deleted in Mission Control stops applying. An
    // empty rule list is not trusted for this: it is far more likely a server
    // hiccup than every signal switched off at once, and pruning on it would
    // wipe every visitor's tags.
    if (defs.length) {
      for (const k of Object.keys(store)) {
        if (!known.has(k)) {
          delete store[k];
          log('dropped (no longer defined)', k);
        }
      }
    } else {
      log('empty rule list — remembered signals kept');
    }
    writeJSON(STORE_KEY, store);
    return earned;
  };

  const fetchDefs = async () => {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), FETCH_TIMEOUT);
    try {
      const r = await fetch(DEFS_URL, { signal: ctrl.signal });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      // A body without a signals array is a failure, not "no rules": reject,
      // so the callers fall back to what is remembered.
      if (!Array.isArray(d?.signals)) throw new Error('malformed rules response');
      const defs = d.signals;
      // An empty list is not cached, so the next page asks again rather than
      // holding a blip for DEFS_FRESH_MS.
      if (defs.length) writeJSON(DEFS_KEY, { ts: Date.now(), defs });
      return defs;
    } finally {
      clearTimeout(timer);
    }
  };

  const tags = () => Object.keys(readStore());

  // ── Resolve ──────────────────────────────────────────────────────────────────
  // Cached rules are applied at once, so the ready promise usually resolves in
  // the same tick and costs the page nothing. Only a first visit (or rules older
  // than a week) waits on the network, and then for FETCH_TIMEOUT at most.

  let earnedHere = [];
  const cached = readJSON(DEFS_KEY);
  const age = cached?.ts ? Date.now() - cached.ts : Infinity;

  let ready;
  if (Array.isArray(cached?.defs) && age < DEFS_MAX_MS) {
    earnedHere = apply(cached.defs);
    ready = Promise.resolve(tags());
    if (age > DEFS_FRESH_MS) {
      fetchDefs()
        .then(defs => {
          earnedHere = earnedHere.concat(apply(defs));
        })
        .catch(e => log('rules refresh failed:', e.message));
    }
  } else {
    ready = fetchDefs()
      .then(defs => {
        earnedHere = apply(defs);
        return tags();
      })
      .catch(e => {
        log('rules unavailable, using remembered signals:', e.message);
        return tags();
      });
  }

  ready.then(t => log('holding', t.length ? t.join(', ') : '(none)'));

  // ── Profile write on pages with no personalization layer ────────────────────
  // Where lrb-personalization.js runs it sends these tags with its own profile
  // upsert. Where it does not (a workout post), a newly earned signal is sent
  // here instead, so the visitor's server-side profile learns it too.

  // Same cookie, format and lifetime as lrb-personalization.js, which reuses
  // whatever it finds — so a visitor first seen here keeps one id everywhere.
  const visitorId = () => {
    try {
      const m = document.cookie.match(/(?:^|; )lrb_vid=([^;]*)/);
      if (m) return decodeURIComponent(m[1]);
      const id = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        const r = (Math.random() * 16) | 0;
        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
      });
      const expires = new Date(Date.now() + 365 * 864e5).toUTCString();
      document.cookie = `lrb_vid=${id}; expires=${expires}; path=/; SameSite=Lax`;
      return /lrb_vid=/.test(document.cookie) ? id : null;
    } catch {
      return null;
    }
  };

  window.addEventListener('load', async () => {
    if (window.__lrbPersonalizationLoaded) return;
    const t = await ready;
    if (!earnedHere.length) return;
    const vid = visitorId();
    if (!vid) return; // cookies blocked; localStorage still carries the signal
    const body = { visitor_id: vid, segment_tags: t };
    if (PREVIEW) body.preview = true;
    fetch(`${BOT_URL}/admin/intelligence/profile`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => {
      /* best-effort */
    });
    log('profile updated with', t.join(', '));
  });

  window.LRB_SIGNALS = {
    ready,
    tags,
    has: k => tags().includes(k),
    clear: () => {
      store = {};
      try {
        localStorage.removeItem(STORE_KEY);
        localStorage.removeItem(DEFS_KEY);
      } catch {
        /* ignore */
      }
    },
  };
})();
